import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { MyEnAbLogo } from '../components/MyEnAbLogo';
import CountrySelect from '../components/CountrySelect';
import { Toaster, toast } from 'sonner';
import SEO from '../components/SEO';
import axios from 'axios';
import { API_URL as API } from '../config';

const userTypes = [
    { value: 'individual_disabled', label: 'Individual Disabled' },
    { value: 'volunteer', label: 'Volunteer' },
    { value: 'service_provider', label: 'Service Provider' },
    { value: 'ngo', label: 'NGO / Non-Profit' },
    { value: 'caregiver', label: 'Caregiver / Family Member' }
];

const disabilityCategories = [
    { value: 'physical', label: 'Physical', color: '#FF5C5C' },
    { value: 'cognitive', label: 'Cognitive / Neurodivergent', color: '#FFD700' },
    { value: 'invisible', label: 'Invisible / Undiagnosed', color: '#F4F4F5' },
    { value: 'psychiatric', label: 'Psychiatric / Emotional', color: '#38BDF8' },
    { value: 'sensory', label: 'Sensory', color: '#34D399' },
    { value: 'multiple', label: 'Multiple Disabilities', color: '#FFFFFF' },
    { value: 'prefer_not_to_say', label: 'Prefer not to say', color: '#71717A' }
];

/**
 * Peek at a JWT payload without verifying the signature.
 * We only use this to read the `type` field — the server always validates fully.
 */
function decodeJwtPayload(token) {
    try {
        const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        return JSON.parse(atob(base64));
    } catch {
        return {};
    }
}

export default function Onboarding() {
    const { user, updateUser, setToken } = useAuth();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    // Whether this is a brand-new SSO user (provisional token) or a
    // returning user who hasn't finished onboarding (real token).
    const [provisionalToken, setProvisionalToken] = useState(null);

    const [formData, setFormData] = useState({
        user_type: user?.user_type || 'individual_disabled',
        location: user?.location || '',
        disability_categories: user?.disability_categories || [],
        onboarding_complete: true,
        privacy_consent: false,
        tos_consent: false
    });

    // On mount: inspect the URL token to decide which flow we're in.
    useEffect(() => {
        const token = searchParams.get('token');
        if (!token) return;

        const payload = decodeJwtPayload(token);

        if (payload.type === 'sso_provisional') {
            // New SSO user — keep token in component state only (never localStorage)
            setProvisionalToken(token);
        } else {
            // Returning user forced back to onboarding — normal session token
            localStorage.setItem('token', token);
            setToken(token);
        }
    }, [searchParams, setToken]);

    // Sync formData once user is loaded (returning-user path only)
    useEffect(() => {
        if (user) {
            setFormData(prev => ({
                ...prev,
                user_type: user.user_type || prev.user_type,
                location: user.location || prev.location,
                disability_categories: user.disability_categories || prev.disability_categories
            }));
        }
    }, [user]);

    const handleCategoryToggle = (category) => {
        setFormData(prev => ({
            ...prev,
            disability_categories: prev.disability_categories.includes(category)
                ? prev.disability_categories.filter(c => c !== category)
                : [...prev.disability_categories, category]
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.privacy_consent || !formData.tos_consent) {
            toast.error('Please accept the Privacy Policy and Terms of Service');
            return;
        }

        setLoading(true);
        try {
            if (provisionalToken) {
                // ── New SSO user path ──────────────────────────────────────
                // Account does not exist yet. Create it now that consent is given.
                const response = await axios.post(`${API}/auth/sso/complete`, {
                    provisional_token: provisionalToken,
                    user_type: formData.user_type,
                    location: formData.location,
                    disability_categories: formData.disability_categories,
                    organization_name: formData.organization_name || '',
                    privacy_consent: true,
                    tos_consent: true,
                });
                const { token: realToken } = response.data;
                // Now it's safe to persist — account exists, consent recorded
                localStorage.setItem('token', realToken);
                axios.defaults.headers.common['Authorization'] = `Bearer ${realToken}`;
                setToken(realToken);
                toast.success('Account created! Welcome to MyEnAb.');
                navigate('/dashboard');
            } else {
                // ── Returning user path ────────────────────────────────────
                // Account exists, just complete the profile.
                await updateUser(formData);
                toast.success('Profile completed!');
                navigate('/dashboard');
            }
        } catch (error) {
            const detail = error.response?.data?.detail;
            toast.error(detail || 'Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#121212] flex items-center justify-center px-4 py-12">
            <SEO title="Complete Your Profile" description="Help us personalize your experience by providing a few more details." />
            <Toaster position="top-center" richColors />

            <div className="w-full max-w-lg">
                <div className="flex justify-center mb-8">
                    <MyEnAbLogo className="w-56 h-14" />
                </div>

                <Card className="bg-[#18181B] border-[#27272A]">
                    <CardHeader>
                        <CardTitle className="font-lexend text-2xl">Complete Your Profile</CardTitle>
                        <CardDescription>
                            Just a few more steps to join our global community
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="space-y-2">
                                <Label id="user-type-label" htmlFor="user_type" className="text-zinc-200">I am a...</Label>
                                <Select
                                    value={formData.user_type}
                                    onValueChange={(value) => setFormData({ ...formData, user_type: value })}
                                >
                                    <SelectTrigger className="input-dark" aria-labelledby="user-type-label">
                                        <SelectValue placeholder="Select your role" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#18181B] border-[#27272A]">
                                        {userTypes.map(type => (
                                            <SelectItem key={type.value} value={type.value}>
                                                {type.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="location" className="text-zinc-200">Country</Label>
                                <CountrySelect
                                    id="location"
                                    value={formData.location}
                                    onChange={(value) => setFormData({ ...formData, location: value })}
                                    className="w-full"
                                />
                            </div>

                            {['individual_disabled', 'caregiver'].includes(formData.user_type) && (
                                <fieldset>
                                    <legend className="text-sm font-medium text-zinc-200 mb-2">Disability Categories (Optional)</legend>
                                    <div className="grid grid-cols-2 gap-3">
                                        {disabilityCategories.map(category => (
                                            <div key={category.value} className="flex items-center space-x-2">
                                                <Checkbox
                                                    id={category.value}
                                                    checked={formData.disability_categories.includes(category.value)}
                                                    onCheckedChange={() => handleCategoryToggle(category.value)}
                                                    className="border-zinc-600 data-[state=checked]:bg-white data-[state=checked]:border-white"
                                                />
                                                <label htmlFor={category.value} className="text-sm cursor-pointer flex items-center gap-2">
                                                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: category.color }} aria-hidden="true"></span>
                                                    {category.label}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </fieldset>
                            )}

                            <div className="space-y-4 pt-2">
                                <div className="flex items-start space-x-2">
                                    <Checkbox
                                        id="privacy_consent"
                                        checked={formData.privacy_consent}
                                        onCheckedChange={(checked) => setFormData({ ...formData, privacy_consent: !!checked })}
                                        className="mt-1 border-zinc-600 data-[state=checked]:bg-white data-[state=checked]:border-white"
                                    />
                                    <label htmlFor="privacy_consent" className="text-sm text-zinc-400 leading-tight cursor-pointer">
                                        I have read and agree to the <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-white hover:underline">Privacy Policy</a> regarding the processing of my personal data.
                                    </label>
                                </div>
                                <div className="flex items-start space-x-2">
                                    <Checkbox
                                        id="tos_consent"
                                        checked={formData.tos_consent}
                                        onCheckedChange={(checked) => setFormData({ ...formData, tos_consent: !!checked })}
                                        className="mt-1 border-zinc-600 data-[state=checked]:bg-white data-[state=checked]:border-white"
                                    />
                                    <label htmlFor="tos_consent" className="text-sm text-zinc-400 leading-tight cursor-pointer">
                                        I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-white hover:underline">Terms of Service</a> and platform rules.
                                    </label>
                                </div>
                            </div>

                            <Button type="submit" className="w-full btn-primary" disabled={loading}>
                                {loading ? 'Creating your account...' : 'Complete Registration'}
                            </Button>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
